// Mock data for the Command Center Tracking demo simulator.
// Not wired to the backend — see PIPELINE_STAGE_BACKEND_MAPPING below for how each
// stage should eventually map to real booking data once this gets wired up.

// The "happy path" a booking progresses through in order, vs. the exception
// outcomes it can exit to from any of the earlier stages (not a next step
// after Completed — kept visually separate in the pipeline stepper for this).
export const PIPELINE_STAGE_GROUPS = {
  main: [
    'Active Trips',
    'Scheduled',
    'Requested',
    'Pending',
    'Completed',
  ],
  exceptions: ['Cancelled', 'Rejected'],
};

export const ACTIVE_TRIP_SUBSTAGES = [
  'Towards Pickup',
  'At Pickup',
  'Towards Drop',
  'At Drop',
  'Untracked',
];

export const PIPELINE_STAGES = [
  'Active Trips',
  'Scheduled',
  'Requested',
  'Pending',
  'Towards Pickup',
  'At Pickup',
  'Towards Drop',
  'At Drop',
  'Untracked',
  'Completed',
  'Cancelled',
  'Rejected',
];

// Mock hospital list for the booking form's "Hospital" field (source-of-booking
// tracking) — not wired to the real GET /hospital/all endpoint (SUPER_ADMIN /
// TENANT_ADMIN only, not accessible to COMMAND_CENTRE_USER). Names reused from
// the dropoff addresses already used elsewhere in this mock data for consistency.
export const MOCK_HOSPITALS = [
  { id: 'hosp-1', name: 'Civil Hospital', city: 'Ahmedabad' },
  { id: 'hosp-2', name: 'Sterling Hospital', city: 'Ahmedabad' },
  { id: 'hosp-3', name: 'CIMS Hospital', city: 'Ahmedabad' },
  { id: 'hosp-4', name: 'Apollo Hospital', city: 'Gandhinagar' },
  { id: 'hosp-5', name: 'Zydus Hospital', city: 'Ahmedabad' },
  { id: 'hosp-6', name: 'Shalby Hospital', city: 'Ahmedabad' },
  { id: 'hosp-7', name: 'SAL Hospital', city: 'Ahmedabad' },
  { id: 'hosp-8', name: 'HCG Cancer Centre', city: 'Ahmedabad' },
];

// Backend `BookingStatus` enum (prisma/schema.prisma): REQUESTED, PENDING, CONFIRMED, ASSIGNED,
// IN_PROGRESS, ARRIVED, AT_PICKUP, AT_DROPOFF, COMPLETED, CANCELLED, NO_SHOW.
// `BookingType`: INSTANT, SCHEDULED.
// Driver-level rejection lives on DriverResponseStatus.DECLINED / FleetAssignmentStatus.REJECTED
// (a per-attempt record, not a persisted booking status) — a booking that gets rejected loops
// back to PENDING for reassignment, or becomes CANCELLED if attempts are exhausted.
export const PIPELINE_STAGE_BACKEND_MAPPING = {
  'Requested': 'BookingStatus.REQUESTED — created from the B2C app, waiting for a dispatcher; becomes PENDING once driver assignment starts',
  'Pending': 'BookingStatus.PENDING — booking created (incl. a future "save later" draft), no driver assigned yet',
  'Scheduled': 'BookingType.SCHEDULED bookings still PENDING/CONFIRMED — future-dated, not yet dispatched',
  'Towards Pickup': 'BookingStatus.ASSIGNED / IN_PROGRESS (direction: pickup) — idle vs live driven by driver location freshness',
  'At Pickup': 'BookingStatus.AT_PICKUP — driver is stationary at the pickup location (arrived_at gets set here now, not on ARRIVED)',
  'Towards Drop': 'BookingStatus.ARRIVED, then IN_PROGRESS (direction: drop) — driver has left pickup, heading to drop-off',
  'At Drop': 'BookingStatus.AT_DROPOFF — driver is stationary at the drop-off location, before "Complete Trip" is tapped',
  'Untracked': 'BookingStatus.ASSIGNED / IN_PROGRESS / ARRIVED / AT_PICKUP / AT_DROPOFF with a driver assigned outside the driver app (e.g. via SMS/manual dispatch) — no GPS feed available, so it can\'t sit in Towards Pickup/At Pickup/Towards Drop/At Drop with the others',
  'Completed': 'BookingStatus.COMPLETED',
  'Cancelled': 'BookingStatus.CANCELLED — by user or driver/fleet',
  'Rejected': 'Derived from DriverResponseStatus.DECLINED / FleetAssignmentStatus.REJECTED before reassignment (not a standalone booking status)',
};

// The inverse of the backend's stageToWhere (commandCenterDispatchService.js)
// — given a booking's own fields, which Trips-page tab does it actually sit
// in right now? Used to send the dispatcher straight to the right tab after
// a Booking-page action completes, instead of dropping them back on Pending
// regardless of what they just did.
//
// Deliberately doesn't attempt 'Rejected' (only ever emerges later,
// asynchronously, from a driver decline — never a direct result of anything
// on the Booking page) or 'Cancelled' (nothing there cancels a booking).
// Callers get 'Pending' as a safe fallback for a status/shape this can't
// confidently classify.
export const deriveTripStage = (booking) => {
  if (!booking) return 'Pending';
  const isScheduled = booking.booking_type === 'SCHEDULED';
  switch (booking.status) {
    case 'COMPLETED':
      return 'Completed';
    case 'CANCELLED':
      return 'Cancelled';
    case 'REQUESTED':
      return 'Requested';
    case 'PENDING':
      return isScheduled ? 'Scheduled' : 'Pending';
    // Towards Pickup and Untracked are both explicitly NON_SCHEDULED stages
    // on the backend, so a Scheduled booking never actually appears in
    // either — it stays under 'Scheduled' even once a driver is
    // confirmed/assigned. (A Scheduled + ASSIGNED booking is a real gap in
    // the backend's own stageToWhere — no stage query matches that
    // combination at all, since 'Scheduled' itself only queries PENDING/
    // CONFIRMED. 'Scheduled' is still the least-wrong tab to send the
    // dispatcher to; it's what they'd expect to check regardless.)
    case 'CONFIRMED':
      if (isScheduled) return 'Scheduled';
      return booking.is_app_tracked ? 'Towards Pickup' : 'Untracked';
    case 'ASSIGNED':
      if (isScheduled) return 'Scheduled';
      return booking.is_app_tracked ? 'Towards Pickup' : 'Untracked';
    case 'AT_PICKUP':
      return booking.is_app_tracked ? 'At Pickup' : 'Untracked';
    case 'AT_DROPOFF':
      return booking.is_app_tracked ? 'At Drop' : 'Untracked';
    case 'ARRIVED':
    case 'IN_PROGRESS':
      if (!booking.is_app_tracked) return 'Untracked';
      return booking.arrived_at ? 'Towards Drop' : 'Towards Pickup';
    default:
      return 'Pending';
  }
};

const iso = (daysFromNow, hh, mm) => {
  const d = new Date(2026, 6, 22 + daysFromNow, hh, mm, 0);
  return d.toISOString();
};

// Driver detail shown in the "Driver & trip details" modal (View button), plus
// a `location` used to seed the trip map (current/last-known driver position).
// `offPlatform` marks a driver assigned outside the driver app (SMS/manual) —
// their `location` is intentionally left null since we have no real GPS feed
// for them, which is what puts the trip in the "Untracked" pipeline stage.
const mkDriver = (name, phone, vehiclePlate, vehicleType, occupied, available, daysFromNow, hh, mm, location, offPlatform = false) => ({
  name, phone, vehiclePlate, vehicleType, occupied, available,
  locationUpdatedAt: iso(daysFromNow, hh, mm),
  location,
  offPlatform,
});

// Mock pickup/dropoff points, centered around Ahmedabad, Gujarat, for the trip map simulator.
const mkPoint = (latitude, longitude, address) => ({ latitude, longitude, address });

// Deterministic pseudo-random generator seeded by a string, so the same trip
// always shows the same set of "nearby drivers" in the Assign Driver modal
// instead of a different list every time it's reopened.
const seededRandom = (seed) => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return () => {
    h = (h * 1103515245 + 12345) >>> 0;
    return (h % 10000) / 10000;
  };
};

const NEARBY_DRIVER_NAMES = [
  'Rakesh Bhatt', 'Chirag Solanki', 'Vishal Patel', 'Jignesh Trivedi',
  'Bhavesh Rana', 'Nilesh Chauhan', 'Paresh Vaghela', 'Anil Thakor',
];

const VEHICLE_TYPE_LABELS = { BLS: 'Basic Life Support', ALS: 'Advanced Life Support' };

// isPartner marks which fleet vendors have a partnership agreement — drivers
// from these vendors can be surfaced first via the "Partner" checkbox in the
// Assign Driver card.
const FLEET_VENDORS = [
  { name: 'Imperial Ambulance Service', isPartner: true },
  { name: 'Rapid Response Fleet', isPartner: true },
  { name: 'CityCare Ambulance Co.', isPartner: false },
  { name: 'MedTrans Emergency Services', isPartner: false },
];

const haversineKm = (a, b) => {
  const R = 6371;
  const dLat = (b.latitude - a.latitude) * Math.PI / 180;
  const dLng = (b.longitude - a.longitude) * Math.PI / 180;
  const s = Math.sin(dLat / 2) ** 2 +
    Math.cos(a.latitude * Math.PI / 180) * Math.cos(b.latitude * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};

// Generates a small pool of "nearby available drivers" around a trip's pickup
// point, for the Assign Driver modal — sorted nearest-first. Deterministic per
// trip (seeded by trip id) so reopening the modal for the same trip is stable.
export const getNearbyDrivers = (trip, count = 5) => {
  if (!trip?.pickup) return [];
  const rand = seededRandom(trip.id);
  const vehicleTypes = ['BLS', 'ALS'];

  const drivers = Array.from({ length: count }, (_, i) => {
    // Scatter within roughly a 0.5-5km radius of pickup
    const angle = rand() * Math.PI * 2;
    const radiusKm = 0.5 + rand() * 4.5;
    const latOffset = (radiusKm / 111) * Math.cos(angle);
    const lngOffset = (radiusKm / (111 * Math.cos((trip.pickup.latitude * Math.PI) / 180))) * Math.sin(angle);
    const location = {
      latitude: trip.pickup.latitude + latOffset,
      longitude: trip.pickup.longitude + lngOffset,
    };
    const distanceKm = haversineKm(trip.pickup, location);
    const vehicleType = vehicleTypes[Math.floor(rand() * vehicleTypes.length)];
    const vendor = FLEET_VENDORS[i % FLEET_VENDORS.length];

    return {
      id: `${trip.id}-driver-${i}`,
      name: NEARBY_DRIVER_NAMES[i % NEARBY_DRIVER_NAMES.length],
      phone: `+9198${Math.floor(10000000 + rand() * 89999999)}`,
      vehiclePlate: `GJ01A${1000 + Math.floor(rand() * 8999)}`,
      vehicleType,
      vehicleTypeLabel: VEHICLE_TYPE_LABELS[vehicleType],
      vendorName: vendor.name,
      isPartnerFleet: vendor.isPartner,
      rating: (4.3 + rand() * 0.6).toFixed(1),
      estimatedFare: Math.round(150 + distanceKm * 25 + rand() * 40),
      location,
      distanceKm,
      etaMinutes: Math.max(2, Math.round((distanceKm / 30) * 60)), // ~30km/h avg city speed
    };
  });

  return drivers.sort((a, b) => a.distanceKm - b.distanceKm);
};

export const MOCK_TRIPS = [
  // Pending
  {
    id: 'TRIP-00000131', stage: 'Pending', customer: { name: 'Anita Desai', phone: '9812345001' },
    driver: null, driverMotion: null, serviceCategory: 'Other', service: 'BLS', priority: 'NON_EMERGENCY',
    condition: 'General Transport', when: { label: 'Requested', at: iso(0, 9, 5) },
    pickup: mkPoint(23.0225, 72.5714, 'Lal Darwaja, Ahmedabad'),
    dropoff: mkPoint(23.0537, 72.6047, 'Civil Hospital, Ahmedabad'),
  },
  {
    id: 'TRIP-00000130', stage: 'Pending', customer: { name: 'Ramesh Iyer', phone: '9812345002' },
    driver: null, driverMotion: null, serviceCategory: 'Emergency', service: 'ALS', priority: 'EMERGENCY',
    condition: 'Chest Pain', when: { label: 'Requested', at: iso(0, 10, 40) },
    pickup: mkPoint(23.0300, 72.5150, 'Satellite, Ahmedabad'),
    dropoff: mkPoint(23.0290, 72.5150, 'Sterling Hospital, Ahmedabad'),
  },
  {
    id: 'TRIP-00000129', stage: 'Pending', customer: { name: 'Farah Sheikh', phone: '9812345003' },
    driver: null, driverMotion: null, serviceCategory: 'Other', service: 'BLS', priority: 'NON_EMERGENCY',
    condition: 'Dialysis Transport', when: { label: 'Requested', at: iso(0, 11, 15) },
    pickup: mkPoint(23.0350, 72.5600, 'Navrangpura, Ahmedabad'),
    dropoff: mkPoint(23.0104, 72.5601, 'Paldi, Ahmedabad'),
  },

  // Scheduled
  {
    id: 'TRIP-00000118', stage: 'Scheduled', customer: { name: 'Vijay Shanti', phone: '9948199921' },
    driver: null, driverMotion: null, serviceCategory: 'Other', service: 'BLS', priority: 'NON_EMERGENCY',
    condition: 'Dead body', when: { label: 'Scheduled', at: iso(0, 13, 38) },
    pickup: mkPoint(22.9964, 72.6034, 'Maninagar, Ahmedabad'),
    dropoff: mkPoint(23.0330, 72.6470, 'Nikol, Ahmedabad'),
  },
  {
    id: 'TRIP-00000117', stage: 'Scheduled', customer: { name: 'Shekar', phone: '7989802988' },
    driver: null, driverMotion: null, serviceCategory: 'Other', service: 'BLS', priority: 'NON_EMERGENCY',
    condition: 'Dead body', when: { label: 'Scheduled', at: iso(0, 12, 18) },
    pickup: mkPoint(23.1180, 72.5980, 'Chandkheda, Ahmedabad'),
    dropoff: mkPoint(23.0500, 72.5580, 'Naranpura, Ahmedabad'),
  },
  {
    id: 'TRIP-00000116', stage: 'Scheduled', customer: { name: 'Vijay Mohan Reddy', phone: '8536853853' },
    driver: mkDriver('Naveen Kumar', '+919133472611', 'GJ01AB1122', 'ALS', false, true, 0, 8, 30, { latitude: 23.0270, longitude: 72.4700 }),
    driverMotion: null, serviceCategory: 'Other', service: 'ALS', priority: 'EMERGENCY',
    condition: 'Pick-Up', when: { label: 'Scheduled', at: iso(0, 9, 12) },
    pickup: mkPoint(23.0270, 72.4700, 'Bopal, Ahmedabad'),
    dropoff: mkPoint(23.0294, 72.4954, 'Apollo Hospital, Gandhinagar Road'),
  },

  // Towards Pickup
  {
    id: 'TRIP-00000112', stage: 'Towards Pickup', customer: { name: 'Kavita Nair', phone: '9812345010' },
    driver: mkDriver('Suresh Patil', '+919133472601', 'GJ01AB1234', 'ALS', true, true, 0, 14, 2, { latitude: 23.0455, longitude: 72.5286 }),
    driverMotion: 'live', serviceCategory: 'Emergency', service: 'ALS',
    priority: 'EMERGENCY', condition: 'Accident Trauma', when: { label: 'ETA to pickup', at: iso(0, 14, 5) },
    pickup: mkPoint(23.0367, 72.5296, 'Vastrapur, Ahmedabad'),
    dropoff: mkPoint(23.0396, 72.5145, 'CIMS Hospital, Science City Road'),
  },
  {
    id: 'TRIP-00000111', stage: 'Towards Pickup', customer: { name: 'Mohammed Yusuf', phone: '9812345011' },
    driver: mkDriver('Dinesh Rao', '+919133472602', 'GJ01AB5678', 'BLS', true, false, 0, 13, 40, { latitude: 23.1080, longitude: 72.5470 }),
    driverMotion: 'idle', serviceCategory: 'Other', service: 'BLS',
    priority: 'NON_EMERGENCY', condition: 'Elderly Transport', when: { label: 'ETA to pickup', at: iso(0, 14, 20) },
    pickup: mkPoint(23.1180, 72.5980, 'Chandkheda, Ahmedabad'),
    dropoff: mkPoint(23.0104, 72.5601, 'Paldi, Ahmedabad'),
  },

  // Towards Drop
  {
    id: 'TRIP-00000105', stage: 'Towards Drop', customer: { name: 'Priya Menon', phone: '9812345020' },
    driver: mkDriver('Arjun Verma', '+919133472603', 'GJ01AB9012', 'ALS', true, true, 0, 13, 45, { latitude: 23.0320, longitude: 72.5170 }),
    driverMotion: 'live', serviceCategory: 'Critical Care', service: 'ALS',
    priority: 'EMERGENCY', condition: 'Cardiac Arrest', when: { label: 'ETA to drop', at: iso(0, 13, 50) },
    pickup: mkPoint(23.0300, 72.5150, 'Satellite, Ahmedabad'),
    dropoff: mkPoint(23.0290, 72.5150, 'Sterling Hospital, Ahmedabad'),
  },
  {
    id: 'TRIP-00000104', stage: 'Towards Drop', customer: { name: 'Sanjay Gupta', phone: '9812345021' },
    driver: mkDriver('Manoj Tiwari', '+919133472604', 'GJ01AB3456', 'BLS', true, false, 0, 12, 55, { latitude: 22.9990, longitude: 72.6020 }),
    driverMotion: 'idle', serviceCategory: 'Other', service: 'BLS',
    priority: 'NON_EMERGENCY', condition: 'Post-Surgery Transfer', when: { label: 'ETA to drop', at: iso(0, 13, 10) },
    pickup: mkPoint(22.9964, 72.6034, 'Maninagar, Ahmedabad'),
    dropoff: mkPoint(23.0330, 72.6470, 'Nikol, Ahmedabad'),
  },

  // Untracked — driver assigned outside the driver app (SMS/manual dispatch), no live GPS feed
  {
    id: 'TRIP-00000109', stage: 'Untracked', customer: { name: 'Ashok Trivedi', phone: '9812345015' },
    driver: mkDriver('Rajendra Solanki', '+919876543210', 'GJ05CD4321', 'BLS', true, false, 0, 13, 15, null, true),
    driverMotion: 'untracked', serviceCategory: 'Other', service: 'BLS',
    priority: 'NON_EMERGENCY', condition: 'General Transport', when: { label: 'Assigned via SMS', at: iso(0, 13, 15) },
    pickup: mkPoint(23.0104, 72.5601, 'Paldi, Ahmedabad'),
    dropoff: mkPoint(23.0350, 72.5600, 'Navrangpura, Ahmedabad'),
  },
  {
    id: 'TRIP-00000108', stage: 'Untracked', customer: { name: 'Geeta Bhatt', phone: '9812345016' },
    driver: mkDriver('Mahesh Chavda', '+919876543211', 'GJ05CD8765', 'ALS', true, false, 0, 12, 40, null, true),
    driverMotion: 'untracked', serviceCategory: 'Emergency', service: 'ALS',
    priority: 'EMERGENCY', condition: 'Fracture', when: { label: 'Assigned via SMS', at: iso(0, 12, 40) },
    pickup: mkPoint(23.1180, 72.5980, 'Chandkheda, Ahmedabad'),
    dropoff: mkPoint(23.0396, 72.5145, 'CIMS Hospital, Science City Road'),
  },

  // Completed
  {
    id: 'TRIP-00000098', stage: 'Completed', customer: { name: 'Lakshmi Narayan', phone: '9812345030' },
    driver: mkDriver('Ravi Kumar', '+919133472605', 'GJ01AB7890', 'BLS', false, true, -1, 18, 32, { latitude: 23.0537, longitude: 72.6047 }),
    driverMotion: null, serviceCategory: 'Other', service: 'BLS',
    priority: 'NON_EMERGENCY', condition: 'Routine Checkup', when: { label: 'Completed', at: iso(-1, 18, 30) },
    pickup: mkPoint(23.0225, 72.5714, 'Lal Darwaja, Ahmedabad'),
    dropoff: mkPoint(23.0537, 72.6047, 'Civil Hospital, Ahmedabad'),
  },
  {
    id: 'TRIP-00000097', stage: 'Completed', customer: { name: 'Harpreet Singh', phone: '9812345031' },
    driver: mkDriver('Ajay Sharma', '+919133472606', 'GJ01AB2345', 'ALS', false, true, -1, 16, 8, { latitude: 23.0290, longitude: 72.5150 }),
    driverMotion: null, serviceCategory: 'Emergency', service: 'ALS',
    priority: 'EMERGENCY', condition: 'Breathing Difficulty', when: { label: 'Completed', at: iso(-1, 16, 5) },
    pickup: mkPoint(23.0300, 72.5150, 'Satellite, Ahmedabad'),
    dropoff: mkPoint(23.0290, 72.5150, 'Sterling Hospital, Ahmedabad'),
  },
  {
    id: 'TRIP-00000096', stage: 'Completed', customer: { name: 'Meera Pillai', phone: '9812345032' },
    driver: mkDriver('Vikram Joshi', '+919133472607', 'GJ01AB6789', 'BLS', false, true, -1, 11, 48, { latitude: 23.0104, longitude: 72.5601 }),
    driverMotion: null, serviceCategory: 'Other', service: 'BLS',
    priority: 'NON_EMERGENCY', condition: 'Dialysis Transport', when: { label: 'Completed', at: iso(-1, 11, 45) },
    pickup: mkPoint(23.0350, 72.5600, 'Navrangpura, Ahmedabad'),
    dropoff: mkPoint(23.0104, 72.5601, 'Paldi, Ahmedabad'),
  },

  // Cancelled
  {
    id: 'TRIP-00000088', stage: 'Cancelled', customer: { name: 'Neha Kulkarni', phone: '9812345040' },
    driver: null, driverMotion: null, serviceCategory: 'Other', service: 'BLS', priority: 'NON_EMERGENCY',
    condition: 'General Transport', when: { label: 'Cancelled', at: iso(-2, 9, 30) },
    pickup: mkPoint(22.9964, 72.6034, 'Maninagar, Ahmedabad'),
    dropoff: mkPoint(23.0330, 72.6470, 'Nikol, Ahmedabad'),
  },
  {
    id: 'TRIP-00000087', stage: 'Cancelled', customer: { name: 'Rohit Malhotra', phone: '9812345041' },
    driver: mkDriver('Sunil Yadav', '+919133472608', 'GJ01AB0123', 'ALS', false, true, -2, 15, 58, { latitude: 23.0300, longitude: 72.5150 }),
    driverMotion: null, serviceCategory: 'Emergency', service: 'ALS',
    priority: 'EMERGENCY', condition: 'Patient No-Show', when: { label: 'Cancelled', at: iso(-2, 15, 55) },
    pickup: mkPoint(23.0300, 72.5150, 'Satellite, Ahmedabad'),
    dropoff: mkPoint(23.0290, 72.5150, 'Sterling Hospital, Ahmedabad'),
  },

  // Rejected
  {
    id: 'TRIP-00000080', stage: 'Rejected', customer: { name: 'Divya Reddy', phone: '9812345050' },
    driver: mkDriver('Kiran Patel', '+919133472609', 'GJ01AB4567', 'BLS', false, true, -3, 10, 18, { latitude: 23.0350, longitude: 72.5600 }),
    driverMotion: null, serviceCategory: 'Other', service: 'BLS',
    priority: 'NON_EMERGENCY', condition: 'Dialysis Transport', when: { label: 'Rejected by driver', at: iso(-3, 10, 15) },
    pickup: mkPoint(23.0350, 72.5600, 'Navrangpura, Ahmedabad'),
    dropoff: mkPoint(23.0104, 72.5601, 'Paldi, Ahmedabad'),
  },
  {
    id: 'TRIP-00000079', stage: 'Rejected', customer: { name: 'Imran Khan', phone: '9812345051' },
    driver: mkDriver('Deepak Nair', '+919133472610', 'GJ01AB8901', 'ALS', false, true, -3, 8, 42, { latitude: 23.0225, longitude: 72.5714 }),
    driverMotion: null, serviceCategory: 'Emergency', service: 'ALS',
    priority: 'EMERGENCY', condition: 'Chest Pain', when: { label: 'Rejected by driver', at: iso(-3, 8, 40) },
    pickup: mkPoint(23.0225, 72.5714, 'Lal Darwaja, Ahmedabad'),
    dropoff: mkPoint(23.0537, 72.6047, 'Civil Hospital, Ahmedabad'),
  },
];